import { ChevronDown, Terminal } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { LocalRunnerCommand } from "./panel-local-runner-command";

interface RunLiveSectionProps {
  targetUrl: string;
  setTargetUrl: (url: string) => void;
  runnerStatus: any;
  selectedRunnerId: string;
  setSelectedRunnerId: (id: string) => void;
  isQueueRun: boolean;
  showConnectCmd: boolean;
  setShowConnectCmd: (show: boolean) => void;
  connectToken: {
    token: string | null;
    error: any;
  };
}

export function RunLiveSection({
  targetUrl,
  setTargetUrl,
  runnerStatus,
  selectedRunnerId,
  setSelectedRunnerId,
  isQueueRun,
  showConnectCmd,
  setShowConnectCmd,
  connectToken,
}: RunLiveSectionProps) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="run-walkthrough-target-url">Target URL</Label>
        <Input
          id="run-walkthrough-target-url"
          type="url"
          placeholder="https://example.com"
          value={targetUrl}
          onChange={(e) => setTargetUrl(e.target.value)}
        />
      </div>

      {runnerStatus && runnerStatus.runners.length > 0 && (
        <div className="space-y-2">
          <Label>Runner</Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedRunnerId("")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition-colors cursor-pointer",
                selectedRunnerId === ""
                  ? "border-primary bg-primary/5 text-foreground"
                  : "border-border text-muted-foreground hover:border-primary/40",
              )}
            >
              Auto
            </button>
            {runnerStatus.runners.map((r: any) => (
              <button
                type="button"
                key={r.id}
                onClick={() => setSelectedRunnerId(r.id)}
                disabled={r.status !== "idle"}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm transition-colors cursor-pointer",
                  selectedRunnerId === r.id
                    ? "border-primary bg-primary/5 text-foreground"
                    : "border-border text-muted-foreground hover:border-primary/40",
                  r.status !== "idle" && "cursor-not-allowed opacity-50",
                )}
              >
                <span
                  className={cn(
                    "inline-block h-1.5 w-1.5 rounded-full",
                    r.status === "idle"
                      ? "bg-mark-helps"
                      : "bg-muted-foreground",
                  )}
                  aria-hidden="true"
                />
                {r.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isQueueRun ? (
        <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground space-y-2">
          <p>
            No runner connected — the run is queued for a teammate&apos;s agent
            (<code className="font-mono text-xs">/iamu:findings</code>). To run
            here instead, connect a runner.
          </p>
          <Collapsible open={showConnectCmd} onOpenChange={setShowConnectCmd}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2 cursor-pointer"
              >
                <Terminal className="h-3 w-3" aria-hidden="true" />
                {showConnectCmd
                  ? "Hide connect command"
                  : "Show connect command"}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-2">
                {connectToken.token ? (
                  <LocalRunnerCommand connectToken={connectToken.token} />
                ) : connectToken.error ? (
                  <p className="text-sm text-muted-foreground">
                    Could not create a connect token. Connecting a runner needs
                    editor access to this project.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Creating a connect token…
                  </p>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      ) : (
        <Collapsible open={showConnectCmd} onOpenChange={setShowConnectCmd}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <Terminal className="h-3 w-3" aria-hidden="true" />
              Run a local runner
              <ChevronDown
                className={cn(
                  "ml-auto h-3 w-3 transition-transform",
                  showConnectCmd && "rotate-180",
                )}
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {connectToken.token ? (
                <LocalRunnerCommand connectToken={connectToken.token} />
              ) : connectToken.error ? (
                <p className="text-sm text-muted-foreground">
                  Could not create a connect token. Connecting a runner needs
                  editor access to this project.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Creating a connect token…
                </p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </>
  );
}
